import { Box, Button, Grid, Link, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import { useLazyGetManifestSearchQuery } from '../../../redux/commonRTK';
import { parseDateAndTime } from '../../../helpers/UtilityHelper';
import PageTitle from '../../../common/PageTitle';
import { getMerchant } from '../../../helpers/ReduxHelpers';
import { FormProvider, RHFTextField } from '../../../common/hook-form';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { apiConstants, postGeneric } from '../../../common/constants';
import Loader from '../../../common/Loader';
import { useSnackbar } from 'notistack';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, BORDER, WHITE, PRIMARY_BLUE, SUBTLE } from '../../../styles/foundation/colors';
import { filterBarSx, primaryBtnSx, secondaryBtnSx, btnRowSx } from '../../../styles/patterns';
import useCourierServiceType from '../../../hooks/useCourierServiceType';

function ManifestList() {
    const [page, setPage] = useState(0);
    const [reload, setReload] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(10);
    const [getManifestList, { data, isFetching }] = useLazyGetManifestSearchQuery()
    const { enqueueSnackbar } = useSnackbar();
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''
    // Resolves a courier's serviceTypePrimary by prozoId — same shared hook the
    // order-list pages (AllOrders, InTransit, Delivered, …) already use.
    const { getServiceType } = useCourierServiceType();

    useEffect(() => {
        getManifestList({
            offset: (page * sizePerPage), limit: sizePerPage,
            andfilter: [{ field: "merchantDetail.key", operator: "eq", value: merchantId }],
        })
        // eslint-disable-next-line
    }, [page, sizePerPage, reload])

    const columns = [
        { field: 'manifestNumber', headerAlign: "left", hideSortIcons: true, headerName: 'Manifest Number', flex: 1 },
        {
            field: 'courierDetail', headerAlign: "left", hideSortIcons: true, headerName: 'Courier', flex: 1,
            renderCell: (params) => {
                const row = params.row;
                let courierParent = row.courierDetail?.parent?.includes('Blended') ? row.actualCourierProviderName : row.courierDetail?.parent;
                return courierParent;
            },
        },
        {
            field: 'serviceType', headerAlign: "left", hideSortIcons: true, headerName: 'Service Type', flex: 1,
            renderCell: (params) => getServiceType(params.row?.courierDetail?.prozoId) || '—',
        },
        {
            field: 'manifest', headerAlign: "left", hideSortIcons: true, headerName: 'Manifest PDF', flex: 1,
            renderCell: (params) => (
                <Link href={params.row?.manifestUrl} target='_blank' sx={{
                    fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                    color: PRIMARY_BLUE, display: 'flex', alignItems: 'center', gap: 0.5,
                }}>
                    View Manifest <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Link>
            ),
        },
        { field: 'totalOrders', headerAlign: "left", hideSortIcons: true, headerName: 'Total Shipments', flex: 0.8 },
        {
            field: 'date', headerAlign: "left", hideSortIcons: true, headerName: 'Manifest Date', flex: 1,
            renderCell: (params) => params.row.generatedDate ? parseDateAndTime(params.row.generatedDate) : ""
        },
    ];

    const methods = useForm({ defaultValues: { awbNumber: '' } });
    const { handleSubmit, reset } = methods

    const onSubmit = async (data) => {
        try {
            const orderPayload = {
                andfilter: [
                    { field: "manifestDetails", operator: "exists", value: true },
                    { field: "awbNumber", operator: "eq", value: data.awbNumber },
                    { field: "merchantDetail.key", operator: "eq", value: merchantId },
                ],
                includes: "manifestDetails.manifestNumber",
                limit: 5000,
            };
            const orderResponse = await postGeneric(apiConstants.ORDER_LISTS, orderPayload)
            if (orderResponse?.data?.data?.length > 0) {
                getManifestList({
                    offset: (page * sizePerPage), limit: sizePerPage,
                    andfilter: [
                        { field: "merchantDetail.key", operator: "eq", value: merchantId },
                        { field: "manifestNumber", operator: "eq", value: String(orderResponse?.data?.data[0]?.manifestDetails?.manifestNumber) }
                    ]
                })
            } else {
                enqueueSnackbar("AWB Number not found", { variant: "error" });
            }
        } catch (error) {
            console.error('Error fetching manifest data:', error);
        }
    }

    return (
        <>
            {isFetching && <Loader />}


            {/* Search bar */}
            <Box sx={{ ...filterBarSx, p: 2, mb: 2, border: 'none', bgcolor: 'transparent' }}>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={4}>
                            <RHFTextField name="awbNumber" label="Search by AWB Number" size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Box sx={btnRowSx}>
                                <LoadingButton variant="contained" size="small" type="submit" sx={primaryBtnSx} startIcon={<SearchIcon />}>
                                    Search
                                </LoadingButton>
                                <Button variant="outlined" size="small" sx={secondaryBtnSx}
                                    startIcon={<RestartAltIcon />} onClick={() => { setReload(reload + 1); reset() }}>
                                    Reset
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </FormProvider>
            </Box>

            {/* Data grid */}
            <ProzoDataGrid
                columns={columns}
                autoHeight={true}
                rows={data?.data || []}
                sizePerPage={sizePerPage}
                setSizePerPage={setSizePerPage}
                setPage={setPage}
                page={page}
                pagination={true}
                hideFooterPagination={false}
                filterMode={"server"}
                rowHeight={60}
                rowCount={data?.count || 0}
                getRowId={(row) => row.id}
            />
        </>
    );
};

export default ManifestList
